import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  CalendarCheck,
  CalendarPlus,
  Eraser,
  Eye,
  FileStack,
  Folder,
  LineChart,
  Monitor,
  Paintbrush,
  PencilLine,
  Receipt,
  Send,
  SlidersHorizontal,
  Sparkles,
  Stethoscope,
  Sunrise,
  UploadCloud,
  UsersRound,
} from 'lucide-react';

import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

import type { PresetCategory } from '../../../contracts/routines.types';

/** Maps the backend's stable icon identifiers to Lucide components. */
const PRESET_ICONS: Record<string, LucideIcon> = {
  sparkles: Sparkles,
  'file-stack': FileStack,
  'calendar-check': CalendarCheck,
  'brush-cleaning': Eraser,
  'book-open': BookOpen,
  'chart-line': LineChart,
  users: UsersRound,
  sunrise: Sunrise,
  'cloud-upload': UploadCloud,
  receipt: Receipt,
  send: Send,
  sliders: SlidersHorizontal,
  monitor: Monitor,
  'calendar-plus': CalendarPlus,
  stethoscope: Stethoscope,
};

/** Marketing download page for the Heidi desktop app (macOS + Windows). */
export const DESKTOP_APP_DOWNLOAD_URL = 'https://www.heidihealth.com/download';

// Object.hasOwn on every lookup below: the keys are backend-supplied, and a
// bare index would reach Object.prototype members ('constructor', …) — a truthy
// function that dodges the fallback and crashes formatMessage / React render.
export function presetIcon(icon: string | null | undefined): LucideIcon {
  if (!icon || !Object.hasOwn(PRESET_ICONS, icon)) return Sparkles;
  return PRESET_ICONS[icon] ?? Sparkles;
}

export const PRESET_CATEGORY_LABELS: Record<PresetCategory, MessageDescriptor> =
  defineMessages({
    insights: {
      defaultMessage: 'Insights',
      id: 'efEYa7qJ7e',
      description: 'Routine preset category chip',
    },
    scribing: {
      defaultMessage: 'Scribing',
      id: '/E+9F6Jmyc',
      description: 'Routine preset category chip',
    },
    cleanup: {
      defaultMessage: 'Cleanup',
      id: 'YnWp8Waa5/',
      description: 'Routine preset category chip',
    },
    learning: {
      defaultMessage: 'Learning',
      id: 'm2lqzVNefV',
      description: 'Routine preset category chip',
    },
    team: {
      defaultMessage: 'Team',
      id: 'j81zjJ++rJ',
      description: 'Routine preset category chip',
    },
  });

// 'more' is not a backend category: it is the sentinel bucket key the explore
// surfaces group uncategorized presets under, so it gets a localized header.
const UNCATEGORIZED_MESSAGES = defineMessages({
  more: {
    defaultMessage: 'More',
    id: 'FjMPrObR8s',
    description: 'Section header for routine presets without a category',
  },
});

export function presetCategoryLabel(intl: IntlShape, category: string): string {
  if (category === 'more') return intl.formatMessage(UNCATEGORIZED_MESSAGES.more);
  // An unmapped category is a raw backend value — show it as-is.
  return Object.hasOwn(PRESET_CATEGORY_LABELS, category)
    ? intl.formatMessage(PRESET_CATEGORY_LABELS[category as PresetCategory])
    : category;
}

/** Category glyphs for the Explore section headers — one per semantic bucket. */
const PRESET_CATEGORY_ICONS: Record<PresetCategory, LucideIcon> = {
  insights: Eye,
  scribing: PencilLine,
  cleanup: Paintbrush,
  learning: BookOpen,
  team: UsersRound,
};

export function presetCategoryIcon(category: string): LucideIcon {
  return Object.hasOwn(PRESET_CATEGORY_ICONS, category)
    ? PRESET_CATEGORY_ICONS[category as PresetCategory]
    : Folder;
}

export function presetCadenceLabel(
  intl: IntlShape,
  preset: {
    suggested_cadence?: 'daily' | 'weekly' | null;
    suggested_event_types?: string[];
  }
): string | null {
  if (preset.suggested_cadence === 'daily') {
    return intl.formatMessage({
      defaultMessage: 'Runs daily',
      id: 'xulF1bAXYe',
      description: 'Routine preset cadence badge',
    });
  }
  if (preset.suggested_cadence === 'weekly') {
    return intl.formatMessage({
      defaultMessage: 'Runs weekly',
      id: '7iloK0muMW',
      description: 'Routine preset cadence badge',
    });
  }
  if ((preset.suggested_event_types ?? []).length > 0) {
    return intl.formatMessage({
      defaultMessage: 'Runs after notes',
      id: 'a/+psaygvK',
      description: 'Routine preset cadence badge: runs after each consult note',
    });
  }
  return intl.formatMessage({
    defaultMessage: 'Run anytime',
    id: 'SVAJ7A0yGG',
    description: 'Routine preset cadence badge: on-demand routine',
  });
}
